import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { MerchantsShowActionSkeleton } from 'pages/Merchants/MerchantsShow/components/MerchantsShowActionSkeleton';
import { MerchantsShowFiltersSkeleton } from 'pages/Merchants/MerchantsShow/components/MerchantsShowFiltersSkeleton';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListController, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { CreateButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import { Link } from 'shared/mui/Link/Link';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import { DateTimeField, FunctionField, SimpleCurrencyField } from 'shared/react-admin/Fields';
import { CurrencyField } from 'shared/react-admin/Fields/CurrencyField/CurrencyField';
import { ListPage } from 'shared/react-admin/Pages';
import { Limit } from 'types/embedded/limit';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { DatagridStyled } from './MerchantsShowLimits.styled';
import { MerchantsShowLimitsCreateDialog } from './MerchantsShowLimitsCreateDialog/MerchantsShowLimitsCreateDialog';
import { MerchantsShowLimitsDeleteDialog } from './MerchantsShowLimitsDeleteDialog/MerchantsShowLimitsDeleteDialog';
import { MerchantsShowLimitsEditDialog } from './MerchantsShowLimitsEditDialog/MerchantsShowLimitsEditDialog';
import { MerchantsShowLimitsFilters } from './MerchantsShowLimitsFilters/MerchantsShowLimitsFilters';
import { MerchantsShowLimitsShowDialog } from './MerchantsShowLimitsShowDialog/MerchantsShowLimitsShowDialog';

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantsShowLimits: React.FC<Props> = ({ merchantId, merchantName }) => {
  const { isLoading } = useListController();
  const translate = useTypedTranslate();
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const showModal = useRecordModal<Limit>();
  const deleteModal = useRecordModal<Limit>();
  const editModal = useRecordModal<Limit>();

  return (
    <ListPage
      actions={[
        hasResourcePermissions(Resources.Limits, [Permission.Full, Permission.Create]) &&
          (isLoading ? (
            <MerchantsShowActionSkeleton />
          ) : (
            <CreateButton
              key="create-limits"
              onClick={(e) => {
                e.preventDefault();
                setOpenCreateDialog(true);
              }}
            />
          )),
      ]}
      empty={false}
      filter={{ merchant: [merchantId] }}
      headerTitle={{
        titleText: translate('app.limits.limits'),
        titleSize: 'smallBold',
      }}
      resource="limits"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      <WithListContext
        render={({ data, isLoading, filterValues }) => {
          const showAlert = !data?.length && !isLoading && !Object.keys(filterValues).length;

          if (showAlert) {
            return (
              <Alert severity="info">
                {translate('app.limits.merchantHasNoLimits', {
                  merchantName,
                })}
              </Alert>
            );
          }

          return (
            <>
              {isLoading ? (
                <MerchantsShowFiltersSkeleton />
              ) : (
                <MerchantsShowLimitsFilters merchantId={merchantId} />
              )}
              {isLoading ? (
                <DatagridSkeleton columnsCount={10} />
              ) : (
                <DatagridStickyContainer>
                  <DatagridStyled bulkActionButtons={false}>
                    <FunctionField
                      label="ID"
                      render={(record: Limit) => (
                        <Link
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            showModal.handleOpenRecordModal(record);
                          }}
                        >
                          {getId(record?.originId)}
                        </Link>
                      )}
                      sortBy="id"
                      source="originId"
                    />
                    {hasResourceAccess(Resources.Gateways) && (
                      <FunctionField
                        cellClassName="column-gateway"
                        label={translate('app.limits.gateway')}
                        render={(record: any) => record?.gateway?.name || EMPTY_FIELD}
                      />
                    )}
                    {hasResourceAccess(Resources.Currencies) && (
                      <CurrencyField
                        alphaCodeSource="currency.alphaCode"
                        cellClassName="column-currency"
                        label={translate('app.limits.currency')}
                        nameSource="currency.name"
                      />
                    )}
                    <SimpleCurrencyField
                      emptyText={EMPTY_FIELD}
                      label={translate('app.limits.minDepositAmount')}
                      source="paymentMin"
                    />
                    <SimpleCurrencyField
                      emptyText={EMPTY_FIELD}
                      label={translate('app.limits.maxDepositAmount')}
                      source="paymentMax"
                    />
                    <SimpleCurrencyField
                      emptyText={EMPTY_FIELD}
                      label={translate('app.limits.minWithdrawalAmount')}
                      source="payoutMin"
                    />
                    <SimpleCurrencyField
                      emptyText={EMPTY_FIELD}
                      label={translate('app.limits.maxWithdrawalAmount')}
                      source="payoutMax"
                    />
                    <DateTimeField label={translate('app.limits.createdAt')} source="createdAt" />
                    <DateTimeField label={translate('app.limits.updatedAt')} source="updatedAt" />
                    <ContextMenu
                      render={(record: Limit) => [
                        hasResourceAccess(Resources.UserActions) && (
                          <ContextMenuEntityUserActionsButton
                            entityName="limit"
                            key="user-action-button"
                          />
                        ),
                        <ContextMenuShowDialogButton
                          key="show-button"
                          onClick={() => showModal.handleOpenRecordModal(record)}
                        />,
                        hasResourcePermissions(Resources.Limits, [
                          Permission.Full,
                          Permission.Update,
                        ]) && (
                          <ContextMenuEditDialogButton
                            key="edit-button"
                            onClick={() => editModal.handleOpenRecordModal(record)}
                          />
                        ),
                        hasResourcePermissions(Resources.Limits, [
                          Permission.Full,
                          Permission.Delete,
                        ]) && (
                          <ContextMenuDeleteButton
                            key="delete-button"
                            onClick={() => deleteModal.handleOpenRecordModal(record)}
                          />
                        ),
                      ]}
                    />
                  </DatagridStyled>
                </DatagridStickyContainer>
              )}
            </>
          );
        }}
      />

      <MerchantsShowLimitsShowDialog
        onClose={showModal.handleCloseRecordModal}
        onDelete={deleteModal.handleOpenRecordModal}
        onEdit={editModal.handleOpenRecordModal}
        open={showModal.openRecordModal}
        record={showModal.record}
      />

      <MerchantsShowLimitsDeleteDialog
        onClose={deleteModal.handleCloseRecordModal}
        open={deleteModal.openRecordModal}
        record={deleteModal.record}
      />

      <MerchantsShowLimitsCreateDialog
        merchantId={merchantId}
        onClose={() => setOpenCreateDialog(false)}
        open={openCreateDialog}
      />

      {editModal.record && (
        <MerchantsShowLimitsEditDialog
          merchantId={merchantId}
          onClose={editModal.handleCloseRecordModal}
          onDelete={deleteModal.handleOpenRecordModal}
          open={editModal.openRecordModal}
          record={editModal.record}
        />
      )}
    </ListPage>
  );
};
